/**
 * ============================================================
 * KEJJA OS — src/pages/notifications/PushSetupCard.tsx
 * Drops into the Preferences page as a top card.
 * ============================================================
 */

import { Smartphone, Check, AlertCircle, Send } from 'lucide-react';
import {
  usePushSupported, usePushStatus, useEnablePush, useDisablePush, useSendTestPush,
} from '@/hooks/usePush';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

export default function PushSetupCard() {
  const supported = usePushSupported();
  const { data: status } = usePushStatus();
  const enableMut = useEnablePush();
  const disableMut = useDisablePush();
  const testMut = useSendTestPush();

  if (!supported) {
    return (
      <Card className="border-muted-foreground/30 bg-muted/30">
        <CardContent className="p-4 flex items-start gap-3">
          <AlertCircle className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
          <div className="text-xs text-muted-foreground">
            Your browser doesn't support push notifications. Try Chrome, Edge, or Firefox on Android. On iOS, install Kejja from Safari's Share menu → "Add to Home Screen".
          </div>
        </CardContent>
      </Card>
    );
  }

  if (status?.permission === 'denied') {
    return (
      <Card className="border-rose-300/50 bg-rose-50/50 dark:bg-rose-950/10">
        <CardContent className="p-4 flex items-start gap-3">
          <AlertCircle className="w-4 h-4 text-rose-600 mt-0.5 flex-shrink-0" />
          <div className="text-xs">
            <div className="font-semibold text-rose-700 dark:text-rose-400">Notifications blocked</div>
            <p className="text-muted-foreground mt-0.5">
              Open your browser settings → Site settings → kejja.saidalink.tech → Notifications → Allow.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-sky-300/50 bg-sky-50/50 dark:bg-sky-950/10">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-lg bg-sky-500/20 text-sky-600 dark:text-sky-400 flex items-center justify-center flex-shrink-0">
            <Smartphone className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold flex items-center gap-2">
              Push notifications
              {status?.subscribed && (
                <span className="text-[10px] bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 px-1.5 py-0.5 rounded font-semibold">
                  <Check className="w-2.5 h-2.5 inline mr-0.5" />ON
                </span>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              Get alerts even when Kejja isn't open. Install the app on your home screen for best experience.
            </p>
            <div className="flex gap-2 mt-3 flex-wrap">
              {!status?.subscribed ? (
                <Button
                  size="sm"
                  className="bg-sky-600 hover:bg-sky-700 text-white text-xs"
                  onClick={() => enableMut.mutate()}
                  disabled={enableMut.isPending}
                >
                  {enableMut.isPending ? 'Enabling…' : 'Enable push notifications'}
                </Button>
              ) : (
                <>
                  <Button
                    size="sm" variant="outline"
                    className="text-xs"
                    onClick={() => testMut.mutate()}
                    disabled={testMut.isPending}
                  >
                    <Send className="w-3 h-3 mr-1" />
                    {testMut.isPending ? 'Sending…' : 'Send test'}
                  </Button>
                  <Button
                    size="sm" variant="ghost"
                    className="text-xs text-muted-foreground"
                    onClick={() => disableMut.mutate()}
                    disabled={disableMut.isPending}
                  >
                    {disableMut.isPending ? 'Disabling…' : 'Disable'}
                  </Button>
                </>
              )}
            </div>
            {enableMut.error && (
              <div className="text-[11px] text-rose-600 mt-2">
                {(enableMut.error as Error).message}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
